import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Loader2,
  Save,
  CheckCircle,
  CreditCard,
  Send,
  Building2,
  Mail,
  Banknote,
  HandCoins,
  Coins,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { paymentSettingsApi } from "../../services/api";

type PaymentMethod = "credit_card" | "etransfer" | "wire" | "cheque" | "eft" | "cash";

const METHOD_META: Record<PaymentMethod, { label: string; Icon: React.FC<any>; description: string }> = {
  credit_card: { label: "Credit Card", Icon: CreditCard, description: "Accept payments via Stripe (Visa, Mastercard, Amex)" },
  etransfer: { label: "Interac e-Transfer", Icon: Send, description: "Canadian instant bank-to-bank transfers" },
  wire: { label: "Wire Transfer", Icon: Building2, description: "Domestic or international wire payments" },
  cheque: { label: "Cheque", Icon: Mail, description: "Payment by mailed cheque" },
  eft: { label: "EFT (Direct Deposit)", Icon: Banknote, description: "Electronic Funds Transfer / direct deposit" },
  cash: { label: "Cash", Icon: Coins, description: "In-person cash payments" },
};

interface EtransferSettings {
  email: string;
  recipientName: string;
  notes: string;
}

interface WireSettings {
  bankName: string;
  accountName: string;
  institutionNumber: string;
  transitNumber: string;
  accountNumber: string;
  swiftCode: string;
  notes: string;
}

interface ChequeSettings {
  payableTo: string;
  mailingAddress: string;
  notes: string;
}

interface EftSettings {
  institutionNumber: string;
  transitNumber: string;
  accountNumber: string;
  notes: string;
}

interface PaymentSettings {
  acceptedMethods: PaymentMethod[];
  etransfer: EtransferSettings;
  wire: WireSettings;
  cheque: ChequeSettings;
  eft: EftSettings;
  defaultPaymentDestination: "trust" | "operating";
  isStripeConnected: boolean;
}

const DEFAULTS: PaymentSettings = {
  acceptedMethods: ["credit_card"],
  etransfer: { email: "", recipientName: "", notes: "" },
  wire: { bankName: "", accountName: "", institutionNumber: "", transitNumber: "", accountNumber: "", swiftCode: "", notes: "" },
  cheque: { payableTo: "", mailingAddress: "", notes: "" },
  eft: { institutionNumber: "", transitNumber: "", accountNumber: "", notes: "" },
  defaultPaymentDestination: "operating",
  isStripeConnected: false,
};

const PaymentSettingsSection: React.FC = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<PaymentSettings>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const res = await paymentSettingsApi.get();
      const d = res.data?.data || res.data;
      setSettings({
        acceptedMethods: d.acceptedMethods || ["credit_card"],
        etransfer: { email: d.etransfer?.email || "", recipientName: d.etransfer?.recipientName || "", notes: d.etransfer?.notes || "" },
        wire: {
          bankName: d.wire?.bankName || "", accountName: d.wire?.accountName || "",
          institutionNumber: d.wire?.institutionNumber || "", transitNumber: d.wire?.transitNumber || "",
          accountNumber: d.wire?.accountNumber || "", swiftCode: d.wire?.swiftCode || "", notes: d.wire?.notes || "",
        },
        cheque: { payableTo: d.cheque?.payableTo || "", mailingAddress: d.cheque?.mailingAddress || "", notes: d.cheque?.notes || "" },
        eft: { institutionNumber: d.eft?.institutionNumber || "", transitNumber: d.eft?.transitNumber || "", accountNumber: d.eft?.accountNumber || "", notes: d.eft?.notes || "" },
        defaultPaymentDestination: d.defaultPaymentDestination || "operating",
        isStripeConnected: !!d.isStripeConnected,
      });
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load payment settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const toggleMethod = (method: PaymentMethod) => {
    setSettings((prev) => {
      const has = prev.acceptedMethods.includes(method);
      return {
        ...prev,
        acceptedMethods: has
          ? prev.acceptedMethods.filter((m) => m !== method)
          : [...prev.acceptedMethods, method],
      };
    });
    setSaved(false);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      const { isStripeConnected, ...payload } = settings;
      await paymentSettingsApi.update(payload);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to save payment settings");
    } finally {
      setSaving(false);
    }
  };

  const isMethodEnabled = (m: PaymentMethod) => settings.acceptedMethods.includes(m);

  const inputCls = "w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-brand transition-colors";
  const labelCls = "block text-xs font-semibold text-slate-500 mb-1";

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate("/settings")} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
          <ArrowLeft className="h-5 w-5 text-slate-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Payment Methods</h1>
          <p className="text-sm text-slate-500 mt-0.5">Configure how clients can pay your firm</p>
        </div>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Accepted Methods */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
        <h2 className="text-sm font-bold text-slate-800 mb-1">Accepted Payment Methods</h2>
        <p className="text-xs text-slate-500 mb-4">Select the methods your firm accepts. Instructions for each enabled method will appear on invoices and the client payment page.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(Object.keys(METHOD_META) as PaymentMethod[]).map((method) => {
            const { label, Icon, description } = METHOD_META[method];
            const enabled = isMethodEnabled(method);
            const isCard = method === "credit_card";
            return (
              <button
                key={method}
                onClick={() => toggleMethod(method)}
                className={`flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all ${
                  enabled
                    ? "border-brand bg-emerald-50/50"
                    : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <div className={`h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 ${enabled ? "bg-brand text-white" : "bg-slate-100 text-slate-400"}`}>
                  <Icon className="h-4.5 w-4.5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-semibold ${enabled ? "text-slate-800" : "text-slate-600"}`}>{label}</span>
                    {enabled && <CheckCircle className="h-3.5 w-3.5 text-brand" />}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{description}</p>
                  {isCard && !settings.isStripeConnected && (
                    <p className="text-[10px] text-amber-600 mt-1 font-medium">Requires Stripe Connect setup</p>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </motion.div>

      {/* e-Transfer Details */}
      {isMethodEnabled("etransfer") && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Send className="h-4 w-4 text-brand" />
            <h2 className="text-sm font-bold text-slate-800">Interac e-Transfer Details</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>e-Transfer Email</label>
              <input
                type="email"
                value={settings.etransfer.email}
                onChange={(e) => { setSettings((p) => ({ ...p, etransfer: { ...p.etransfer, email: e.target.value } })); setSaved(false); }}
                placeholder="payments@yourfirm.com"
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Recipient Name</label>
              <input
                type="text"
                value={settings.etransfer.recipientName}
                onChange={(e) => { setSettings((p) => ({ ...p, etransfer: { ...p.etransfer, recipientName: e.target.value } })); setSaved(false); }}
                placeholder="Janssen Law Professional Corp."
                className={inputCls}
              />
            </div>
            <div className="md:col-span-2">
              <label className={labelCls}>Instructions / Notes</label>
              <input
                type="text"
                value={settings.etransfer.notes}
                onChange={(e) => { setSettings((p) => ({ ...p, etransfer: { ...p.etransfer, notes: e.target.value } })); setSaved(false); }}
                placeholder="Please include invoice number as the message"
                className={inputCls}
              />
            </div>
          </div>
        </motion.div>
      )}

      {/* Wire Transfer Details */}
      {isMethodEnabled("wire") && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="h-4 w-4 text-brand" />
            <h2 className="text-sm font-bold text-slate-800">Wire Transfer Details</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Bank Name</label>
              <input type="text" value={settings.wire.bankName} onChange={(e) => { setSettings((p) => ({ ...p, wire: { ...p.wire, bankName: e.target.value } })); setSaved(false); }} placeholder="TD Canada Trust" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Account Name</label>
              <input type="text" value={settings.wire.accountName} onChange={(e) => { setSettings((p) => ({ ...p, wire: { ...p.wire, accountName: e.target.value } })); setSaved(false); }} placeholder="Janssen Law Trust Account" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Institution Number</label>
              <input type="text" value={settings.wire.institutionNumber} onChange={(e) => { setSettings((p) => ({ ...p, wire: { ...p.wire, institutionNumber: e.target.value } })); setSaved(false); }} placeholder="004" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Transit Number</label>
              <input type="text" value={settings.wire.transitNumber} onChange={(e) => { setSettings((p) => ({ ...p, wire: { ...p.wire, transitNumber: e.target.value } })); setSaved(false); }} placeholder="12345" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Account Number</label>
              <input type="text" value={settings.wire.accountNumber} onChange={(e) => { setSettings((p) => ({ ...p, wire: { ...p.wire, accountNumber: e.target.value } })); setSaved(false); }} placeholder="1234567" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>SWIFT Code</label>
              <input type="text" value={settings.wire.swiftCode} onChange={(e) => { setSettings((p) => ({ ...p, wire: { ...p.wire, swiftCode: e.target.value } })); setSaved(false); }} placeholder="TDOMCATTTOR" className={inputCls} />
            </div>
            <div className="md:col-span-2">
              <label className={labelCls}>Notes</label>
              <input type="text" value={settings.wire.notes} onChange={(e) => { setSettings((p) => ({ ...p, wire: { ...p.wire, notes: e.target.value } })); setSaved(false); }} placeholder="Please reference invoice number" className={inputCls} />
            </div>
          </div>
        </motion.div>
      )}

      {/* Cheque Details */}
      {isMethodEnabled("cheque") && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Mail className="h-4 w-4 text-brand" />
            <h2 className="text-sm font-bold text-slate-800">Cheque Details</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Payable To</label>
              <input type="text" value={settings.cheque.payableTo} onChange={(e) => { setSettings((p) => ({ ...p, cheque: { ...p.cheque, payableTo: e.target.value } })); setSaved(false); }} placeholder="Janssen Law Professional Corp." className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Mailing Address</label>
              <input type="text" value={settings.cheque.mailingAddress} onChange={(e) => { setSettings((p) => ({ ...p, cheque: { ...p.cheque, mailingAddress: e.target.value } })); setSaved(false); }} placeholder="123 Main St, Suite 200, Toronto ON M5H 2N2" className={inputCls} />
            </div>
            <div className="md:col-span-2">
              <label className={labelCls}>Notes</label>
              <input type="text" value={settings.cheque.notes} onChange={(e) => { setSettings((p) => ({ ...p, cheque: { ...p.cheque, notes: e.target.value } })); setSaved(false); }} placeholder="Please include invoice number on the memo line" className={inputCls} />
            </div>
          </div>
        </motion.div>
      )}

      {/* EFT Details */}
      {isMethodEnabled("eft") && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Banknote className="h-4 w-4 text-brand" />
            <h2 className="text-sm font-bold text-slate-800">EFT / Direct Deposit Details</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={labelCls}>Institution Number</label>
              <input type="text" value={settings.eft.institutionNumber} onChange={(e) => { setSettings((p) => ({ ...p, eft: { ...p.eft, institutionNumber: e.target.value } })); setSaved(false); }} placeholder="004" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Transit Number</label>
              <input type="text" value={settings.eft.transitNumber} onChange={(e) => { setSettings((p) => ({ ...p, eft: { ...p.eft, transitNumber: e.target.value } })); setSaved(false); }} placeholder="12345" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Account Number</label>
              <input type="text" value={settings.eft.accountNumber} onChange={(e) => { setSettings((p) => ({ ...p, eft: { ...p.eft, accountNumber: e.target.value } })); setSaved(false); }} placeholder="1234567" className={inputCls} />
            </div>
            <div className="md:col-span-3">
              <label className={labelCls}>Notes</label>
              <input type="text" value={settings.eft.notes} onChange={(e) => { setSettings((p) => ({ ...p, eft: { ...p.eft, notes: e.target.value } })); setSaved(false); }} placeholder="Please reference invoice number" className={inputCls} />
            </div>
          </div>
        </motion.div>
      )}

      {/* Default Payment Destination */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
        <h2 className="text-sm font-bold text-slate-800 mb-1">Default Payment Destination</h2>
        <p className="text-xs text-slate-500 mb-4">Where should online card payments (Stripe) be routed by default?</p>
        <div className="flex gap-3">
          {(["operating", "trust"] as const).map((dest) => (
            <button
              key={dest}
              onClick={() => { setSettings((p) => ({ ...p, defaultPaymentDestination: dest })); setSaved(false); }}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 text-sm font-medium transition-all ${
                settings.defaultPaymentDestination === dest
                  ? "border-brand bg-emerald-50/50 text-slate-800"
                  : "border-slate-200 text-slate-500 hover:border-slate-300"
              }`}
            >
              <HandCoins className="h-4 w-4" />
              {dest === "operating" ? "Operating Account" : "Trust Account"}
            </button>
          ))}
        </div>
      </motion.div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold text-white bg-brand hover:bg-brand-hover transition-colors disabled:opacity-70"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : saved ? (
            <CheckCircle className="h-4 w-4" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          {saving ? "Saving..." : saved ? "Saved" : "Save Settings"}
        </button>
      </div>
    </div>
  );
};

export default PaymentSettingsSection;
